/**
 * Shared tenant targeting types and helpers for segment definitions and automation templates.
 * `targetedTenantsFilter`: null = all tenants, [] = none (draft), non-empty = those tenant ids.
 */
export var TenantFilterTypeEnum;
(function (TenantFilterTypeEnum) {
    TenantFilterTypeEnum["ALL"] = "all";
    TenantFilterTypeEnum["NONE"] = "none";
    TenantFilterTypeEnum["FILTERED"] = "filtered";
})(TenantFilterTypeEnum || (TenantFilterTypeEnum = {}));
/**
 * null/undefined = ALL; non-array = ALL (defensive); empty array [] = NONE; non-empty array = FILTERED.
 * When FILTERED is returned, the value is guaranteed to be a non-empty string[].
 */
export function getTenantFilterType(targetedTenantsFilter) {
    if (targetedTenantsFilter === null ||
        targetedTenantsFilter === undefined ||
        !Array.isArray(targetedTenantsFilter)) {
        return TenantFilterTypeEnum.ALL;
    }
    if (targetedTenantsFilter.length === 0)
        return TenantFilterTypeEnum.NONE;
    return TenantFilterTypeEnum.FILTERED;
}
/**
 * Per-tenant sync state of a library item (automation template or segment definition) relative to a
 * single tenant, derived from its tenant membership plus (for automations) the tenant's
 * BusinessAutomation status. Distinct from the global list status.
 *
 * - DRAFT: synced to no tenants and not including new ones; syncable.
 * - NOT_SYNCED: the tenant is not a member.
 * - AVAILABLE: the tenant is a member, automation materialized but not enabled (or job pending / archived).
 * - ACTIVE: enabled BusinessAutomation (automations), or an active BusinessSegment (segments).
 * - FAILED: synced but materialization failed — a failed BusinessAutomation, or a selected segment
 *   with no active BusinessSegment.
 */
export var TenantSyncStateEnum;
(function (TenantSyncStateEnum) {
    TenantSyncStateEnum["DRAFT"] = "draft";
    TenantSyncStateEnum["NOT_SYNCED"] = "not_synced";
    TenantSyncStateEnum["AVAILABLE"] = "available";
    TenantSyncStateEnum["ACTIVE"] = "active";
    TenantSyncStateEnum["FAILED"] = "failed";
    TenantSyncStateEnum["SYNCING"] = "syncing";
})(TenantSyncStateEnum || (TenantSyncStateEnum = {}));
/**
 * Resolve an editor's targeting save into the item's tenants. An all-tenants filter means every
 * platform tenant except the excluded ones. When the editor sends what it loaded, only the tenants
 * it added or removed change, so tenants added or removed elsewhere since then are kept.
 */
export function applyTenantMembershipEdit(edit) {
    const { live, platformTenantIds } = edit;
    const savesFilter = edit.targetedTenantsFilter !== undefined;
    const savesExclusions = edit.excludedTenantIds !== undefined;
    if (!savesFilter && !savesExclusions)
        return live;
    const autoIncludeNewTenants = savesFilter
        ? edit.targetedTenantsFilter === null
        : live.autoIncludeNewTenants;
    const includedTenantIds = (filter, excludedTenantIds) => {
        if (filter === null || (filter === undefined && autoIncludeNewTenants)) {
            const excluded = new Set(excludedTenantIds ?? []);
            return platformTenantIds.filter(id => !excluded.has(id));
        }
        return filter ?? live.tenantIds;
    };
    const nextTenantIds = includedTenantIds(edit.targetedTenantsFilter, edit.excludedTenantIds);
    const editorSentWhatItLoaded = savesFilter
        ? edit.previousTargetedTenantsFilter !== undefined
        : edit.previousExcludedTenantIds !== undefined;
    if (!editorSentWhatItLoaded) {
        return { tenantIds: Array.from(new Set(nextTenantIds)), autoIncludeNewTenants };
    }
    const loadedTenantIds = new Set(includedTenantIds(savesFilter ? edit.previousTargetedTenantsFilter : undefined, edit.previousExcludedTenantIds));
    const next = new Set(nextTenantIds);
    const removedTenantIds = new Set([...loadedTenantIds].filter(id => !next.has(id)));
    const addedTenantIds = nextTenantIds.filter(id => !loadedTenantIds.has(id));
    const keptLiveTenantIds = live.tenantIds.filter(id => !removedTenantIds.has(id));
    return {
        tenantIds: Array.from(new Set([...keptLiveTenantIds, ...addedTenantIds])),
        autoIncludeNewTenants,
    };
}
/**
 * Whether some tenant could get both items. Two items that include new tenants always overlap:
 * the next tenant created gets both.
 */
export function tenantMembershipsOverlap(first, second) {
    if (first.autoIncludeNewTenants && second.autoIncludeNewTenants)
        return true;
    const secondTenantIds = new Set(second.tenantIds);
    return first.tenantIds.some(id => secondTenantIds.has(id));
}
//# sourceMappingURL=tenant-filter.js.map